/**
 * Environment Configuration
 * Centralized configuration for API and app settings
 *
 * In container deployments the values Vite inlined at build time are overridden
 * by window.__APP_ENV__, which /env.js populates on container start. That lets a
 * single image be promoted across environments. See docker/README.md.
 */

type RuntimeEnv = Record<string, string | undefined>;

const runtimeEnv: RuntimeEnv =
  (typeof window !== "undefined" &&
    (window as Window & { __APP_ENV__?: RuntimeEnv }).__APP_ENV__) ||
  {};

/**
 * Reads a VITE_ variable, preferring the runtime value injected into the
 * container over the one baked in at build time.
 */
const readEnv = (key: string): string | undefined =>
  runtimeEnv[key] || (import.meta.env as RuntimeEnv)[key];

interface EnvConfig {
  apiBaseUrl: string;
  apiTimeout: number;
  isDevelopment: boolean;
  isProduction: boolean;
}

const getEnvConfig = (): EnvConfig => {
  const isDevelopment =
    import.meta.env.DEV || import.meta.env.MODE === "development";
  const isProduction =
    import.meta.env.PROD || import.meta.env.MODE === "production";

  // Get API base URL from environment variable only — no hard-coded fallback.
  const rawApiBaseUrl =
    readEnv("VITE_API_BASE_URL") || readEnv("VITE_REACT_APP_API_BASE_URL");

  if (!rawApiBaseUrl && isProduction) {
    throw new Error(
      "VITE_API_BASE_URL is required in production. Set it in your environment file.",
    );
  }

  const apiBaseUrl = (rawApiBaseUrl ?? "").replace(/\/$/, ""); // Remove trailing slash
  const apiTimeout = Number(readEnv("VITE_API_TIMEOUT") || "30000");

  return {
    apiBaseUrl,
    apiTimeout,
    isDevelopment,
    isProduction,
  };
};

export const env = getEnvConfig();

// Export individual values for convenience
export const API_BASE_URL = env.apiBaseUrl;
export const API_TIMEOUT = env.apiTimeout;
export const IS_DEVELOPMENT = env.isDevelopment;
export const IS_PRODUCTION = env.isProduction;
